import {
    Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import type { CanalBot, GranularidadeSerie, SerieBot } from '@/services/api/analytics-bot';
import { n } from '../format';
import Avisos from './Avisos';
import EstadoBloco from './EstadoBloco';

interface TipProps {
    active?: boolean;
    label?: string | number;
    payload?: { dataKey?: string | number; name?: string; value?: number | null; color?: string }[];
}

function Tip({ active, payload, label }: TipProps) {
    if (!active || !payload?.length) return null;
    const vals = payload.filter(p => p.value != null);
    if (!vals.length) return null;
    return (
        <div className="tt">
            <div className="tt-t">{label}</div>
            {vals.map(p => (
                <div className="tt-r" key={String(p.dataKey)}>
                    <span style={{ color: p.color }}>{p.name}</span>
                    <span className="num" style={{ fontWeight: 600 }}>{n(p.value)}</span>
                </div>
            ))}
        </div>
    );
}

interface GraficoCliquesProps {
    serie: SerieBot | undefined;
    granul: GranularidadeSerie;
    canal: CanalBot;
    umDia: boolean;
    diaParcial: boolean;
    carregando: boolean;
    atualizando: boolean;
    erro: boolean;
    onTentar: () => void;
    onGranul: (g: GranularidadeSerie) => void;
    onCanal: (c: CanalBot) => void;
}

/** Cliques por hora/dia: broadcast é barra, flow é linha. Hora futura vem null e fica vazia. */
export default function GraficoCliques({
    serie, granul, canal, umDia, diaParcial, carregando, atualizando, erro, onTentar, onGranul, onCanal,
}: GraficoCliquesProps) {
    const porHora = serie?.granularidade === 'hora';
    const pontos = serie?.pontos ?? [];

    return (
        <div className="card">
            <div className="card-h">
                <div>
                    <div className="card-t">Cliques {porHora ? 'por hora' : 'por dia'}</div>
                    <div className="card-s">
                        {porHora
                            ? (diaParcial ? 'Horas que ainda não aconteceram ficam vazias, não zeradas.' : 'Distribuição do dia inteiro.')
                            : `${pontos.length} dias no período.`}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <div className="seg">
                        {([['auto', 'Auto'], ['hora', 'Por hora'], ['dia', 'Por dia']] as const).map(([k, l]) => (
                            <button key={k} type="button" data-on={granul === k ? 1 : 0}
                                onClick={() => onGranul(k)} disabled={k === 'dia' && umDia}>{l}</button>
                        ))}
                    </div>
                    <div className="seg">
                        {([['ambos', 'Os dois'], ['flow', 'Só flow'], ['broadcast', 'Só broadcast']] as const).map(([k, l]) => (
                            <button key={k} type="button" data-on={canal === k ? 1 : 0} onClick={() => onCanal(k)}>{l}</button>
                        ))}
                    </div>
                </div>
            </div>
            {!serie ? (
                <EstadoBloco carregando={carregando} erro={erro} onTentar={onTentar} />
            ) : (
                <div style={{ padding: '16px 12px 8px 0' }} data-loading={atualizando ? 1 : 0}>
                    <ResponsiveContainer width="100%" height={218}>
                        <ComposedChart data={pontos} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                            <CartesianGrid stroke="#F0F2F5" vertical={false} />
                            <XAxis dataKey="rot" tick={{ fontSize: 11, fill: '#8C93A0' }} axisLine={{ stroke: '#E3E6EB' }} tickLine={false}
                                interval={pontos.length > 16 ? Math.floor(pontos.length / 12) : 0} />
                            <YAxis tick={{ fontSize: 11, fill: '#8C93A0' }} axisLine={false} tickLine={false} width={52}
                                tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))} />
                            <Tooltip content={<Tip />} cursor={{ fill: 'rgba(47,91,234,.05)' }} />
                            {canal !== 'flow' && (
                                <Bar dataKey="broadcast" name="Broadcast" fill="#12805C" radius={[3, 3, 0, 0]} maxBarSize={26} isAnimationActive={false} />
                            )}
                            {canal === 'flow' && (
                                <Bar dataKey="flow" name="Flow" fill="#0E7490" radius={[3, 3, 0, 0]} maxBarSize={26} isAnimationActive={false} />
                            )}
                            {canal === 'ambos' && (
                                <Line type="monotone" dataKey="flow" name="Flow" stroke="#0E7490" strokeWidth={2.2}
                                    dot={false} connectNulls={false} isAnimationActive={false} />
                            )}
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            )}
            <Avisos avisos={serie?.avisos} />
        </div>
    );
}
